import React, { useState, useEffect } from 'react';
import { Modal, Table, Typography, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useGetUploadHistoryQuery, UploadHistoryItem } from '../../../../store/reports/reports.actions';
import { JsonViewer } from 'components/JsonViewer/JsonViewer';

const { Title } = Typography;

interface UploadHistoryModalProps {
  visible: boolean;
  onClose: () => void;
}

export const UploadHistoryModal: React.FC<UploadHistoryModalProps> = ({
  visible,
  onClose,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const { data: uploadHistoryResponse, isLoading, refetch } = useGetUploadHistoryQuery({
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  });

  const handleRefresh = () => {
    refetch();
  };

  const renderUploadStatus = (status: string) => {
    return (
        <JsonViewer initiallyCollapsed showCopyButton={false} data={JSON.parse(status)} />
    );
  };

  const columns: ColumnsType<UploadHistoryItem> = [
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 130,
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 180,
    },
    {
      title: 'File Info',
      dataIndex: 'fileInfo',
      key: 'fileInfo',
      width: 200,
    },
    {
      title: 'Upload Status',
      dataIndex: 'uploadStatus',
      key: 'uploadStatus',
      width: 240,
      render: renderUploadStatus,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: 160,
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  const modalTitle = (
    <Space>
      <Title level={4} style={{ margin: 0 }}>Upload History</Title>
      <Button 
        icon={<ReloadOutlined />} 
        onClick={handleRefresh}
        loading={isLoading}
        size="small"
      >
        Refresh
      </Button>
    </Space>
  );

  return (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Table
        columns={columns}
        dataSource={uploadHistoryResponse?.results || []}
        loading={isLoading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: uploadHistoryResponse?.paging?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 850, y: 600 }}
      />
    </Modal>
  );
}; 